<template>
	<t-header>
		<t-head-menu class="header-menu" :value="defaultMenuValue">
			<template #logo>
				<img width="156" class="logo" src="@/assets/images/logo-light.png" alt="logo" />
			</template>

			<!-- 桌面端菜单 -->
			<MenuItem v-if="!isMobile" :menuItems="menuItems" />

			<!-- 移动端汉堡菜单 -->
			<div class="mobile-menu" v-else>
				<t-button variant="text" class="hamburger-btn" @click="toggleMobileMenu">
					<t-icon :name="isMobileMenuOpen ? 'close' : 'menu-unfold'" />
				</t-button>
			</div>

			<template #operations>
				<template v-if="!isMobile">
					<FullScreen />
				</template>
				<t-button theme="primary" variant="text" class="manual-btn" @click="openManual"> 操作手册 </t-button>
				<Dropdown />
			</template>
		</t-head-menu>

		<!-- 移动端菜单抽屉 -->
		<t-drawer v-model:visible="isMobileMenuOpen" placement="left" size="280px" class="mobile-drawer" :footer="false">
			<div class="mobile-menu-content">
				<div class="mobile-menu-header">
					<img width="120" class="logo" src="@/assets/images/logo-light.png" alt="logo" />
				</div>
				<div class="mobile-menu-items">
					<div v-for="item in menuItems" :key="item.path" class="mobile-menu-item" :class="{ active: defaultMenuValue === item.path }" @click="handleMobileMenuClick(item)">
						{{ item.title }}
					</div>
				</div>
			</div>
		</t-drawer>
	</t-header>
</template>

<script setup lang="ts">
import { computed, ref } from 'vue'
import currentRoute from '@/router'
import MenuItem from './MenuItem.vue'
import { useRoute, useRouter } from 'vue-router'
import FullScreen from './components/fullScreen.vue'
import Dropdown from './components/dropdown.vue'
import store from '@/store'

const { platform } = store()
const isMobile = computed(() => platform.getIsMobile)

const route = useRoute()
const router = useRouter()

// 移动端菜单状态
const isMobileMenuOpen = ref(false)

interface Route {
	meta: {
		showMenu: boolean
		title: string
	}
	path: string
	name: string
}

// 获取第一级菜单
const menuItems = computed(() => {
	return currentRoute
		.getRoutes()
		.filter((route: Route) => route.meta.showMenu === true)
		.map((route: Route) => {
			return {
				path: route.path,
				name: route.name,
				title: route.meta.title,
			}
		})
})

// 计算当前应该选中的菜单项
const defaultMenuValue = computed(() => {
	const currentPath = route.path

	// 如果是根路径，返回首页路径
	if (currentPath === '/') {
		return '/'
	}

	// 获取当前路径的第一级路径
	const firstLevelPath = '/' + currentPath.split('/')[1]

	// 检查这个路径是否在菜单项中
	const menuItem = menuItems.value.find((item: { path: string; name: string; title: string }) => item.path === firstLevelPath)

	return menuItem ? menuItem.path : '/'
})

// 切换移动端菜单
const toggleMobileMenu = () => {
	isMobileMenuOpen.value = !isMobileMenuOpen.value
}

// 移动端菜单点击处理
const handleMobileMenuClick = (item: { path: string; name: string; title: string }) => {
	router.push(item.path)
	isMobileMenuOpen.value = false
}

// 监听路由变化，关闭移动端菜单
// watch(() => route.path, () => {
//     isMobileMenuOpen.value = false
// })

const openManual = () => {
	window.open('https://docs.qq.com/doc/DTkhCZkRIV1FvTkxz', '_blank')
}
</script>

<style scoped lang="scss">
.header-menu {
	&.t-head-menu {
		background-color: var(--td-brand-color-active);
	}

	// 桌面端菜单
	.desktop-menu {
		display: block;
	}

	// 移动端菜单按钮
	.mobile-menu {
		display: none;

		.hamburger-btn {
			color: #fff;
			padding: 8px;

			&:hover {
				color: var(--td-brand-color-active);
				background-color: rgba(255, 255, 255, 0.1);
			}
		}
	}

	// 菜单项样式
	:deep(.t-menu__item) {
		color: #fff;
		transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
		margin: 0 4px;
		border-radius: 6px;
		position: relative;

		&.t-is-active {
			color: var(--td-brand-color-active) !important;
			background-color: #fff !important;
			font-weight: 600;
			box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);

			&::after {
				content: '';
				position: absolute;
				bottom: -2px;
				left: 50%;
				transform: translateX(-50%);
				width: 20px;
				height: 2px;
				background-color: var(--td-brand-color-active);
				border-radius: 1px;
			}
		}

		&:hover:not(.t-is-disabled):not(.t-is-opened) {
			color: var(--td-brand-color-active);
			background-color: rgba(255, 255, 255, 0.85);
			transform: translateY(-1px);
		}

		&:active {
			transform: translateY(0);
		}
	}

	// 操作图标样式
	:deep(.t-menu__operations-icon) {
		color: #fff;
		transition: all 0.3s ease;

		&:hover {
			color: var(--td-brand-color-active);
			transform: scale(1.1);
		}
	}
	.manual-btn {
		color: #fff;
		font-size: 14px;
		&:hover {
			color: var(--td-brand-color-active);
		}
	}
	.notify-btn {
		color: #fff;
		font-size: 18px;
		&:hover {
			color: var(--td-brand-color-active);
		}
	}
}

.notify-panel {
	width: 360px;
	max-height: 480px;
	overflow: hidden;
	background: #fff;
	border-radius: 8px;
	box-shadow: 0 8px 24px rgba(31, 45, 61, 0.14);
}
.notify-head {
	display: flex;
	align-items: center;
	justify-content: space-between;
	padding: 12px 16px;
	border-bottom: 1px solid #e3e9f0;
	font-weight: 600;
	font-size: 15px;
	color: #1d2b3a;
}
.notify-loading,
.notify-empty {
	padding: 30px 16px;
	text-align: center;
	color: #8a99a8;
	font-size: 13px;
}
.notify-list {
	max-height: 360px;
	overflow-y: auto;
}
.notify-item {
	padding: 12px 16px;
	border-bottom: 1px solid #f0f2f5;
	cursor: pointer;
	transition: background 0.15s ease;
	&:hover {
		background: #f5f8fc;
	}
	&.unread {
		background: #f0f7ff;
		.notify-title {
			font-weight: 600;
			color: #1d2b3a;
		}
	}
}
.notify-title {
	font-size: 14px;
	color: #3a4e61;
	line-height: 1.5;
	overflow: hidden;
	text-overflow: ellipsis;
	white-space: nowrap;
}
.notify-time {
	margin-top: 4px;
	font-size: 12px;
	color: #8a99a8;
}
.notify-more {
	padding: 10px 16px;
	text-align: center;
	border-top: 1px solid #f0f2f5;
}

// 移动端菜单抽屉样式
:deep(.t-drawer__content-wrapper) {
	background-color: var(--td-brand-color-active);
}
.mobile-menu-content {
	height: 100%;
	display: flex;
	flex-direction: column;

	.mobile-menu-header {
		padding: 20px;
		border-bottom: 1px solid rgba(255, 255, 255, 0.1);
		text-align: center;

		.logo {
			filter: brightness(0) invert(1);
		}
	}

	.mobile-menu-items {
		flex: 1;
		padding: 20px 0;

		.mobile-menu-item {
			padding: 16px 24px;
			color: #fff;
			font-size: 16px;
			cursor: pointer;
			transition: all 0.3s ease;
			border-left: 3px solid transparent;

			&:hover {
				background-color: rgba(255, 255, 255, 0.1);
				border-left-color: rgba(255, 255, 255, 0.3);
			}

			&.active {
				background-color: rgba(255, 255, 255, 0.15);
				border-left-color: #fff;
				font-weight: 600;
			}
		}
	}
}

// 响应式设计
@media (max-width: 768px) {
	.header-menu {
		.desktop-menu {
			display: none;
		}

		.mobile-menu {
			display: block;
		}

		// 调整logo大小
		:deep(.t-head-menu__logo) {
			img {
				width: 120px !important;
			}
		}

		// 调整操作区域
		:deep(.t-menu__operations) {
			gap: 8px;
		}
	}
}

@media (max-width: 480px) {
	.header-menu {
		:deep(.t-head-menu__logo) {
			img {
				width: 100px !important;
			}
		}
	}
}
</style>
